/**
 * Mobile scroll-triggered autoplay for the shared Image block's hover-video overlay.
 *
 * Opt-in only: acts on elements carrying data-mobile-scroll-autoplay="true"
 * (set in snippets/snip_media_with_hover_overlay.liquid when the block's
 * "mobile_scroll_autoplay" setting is enabled). Desktop (hover-capable
 * pointers) and every other Image block instance are entirely untouched —
 * this script does not modify any existing hover/video markup or CSS.
 *
 * Behavior: once >=70% of the block is continuously in view for ~2s,
 * fades in the existing hover-overlay video (reusing its own opacity
 * transition). When the block becomes mostly out of view (<=30% visible),
 * pauses the video and fades the overlay back out, revealing the base
 * image again. Re-entering view restarts the same logic.
 *
 * Two things this has to work around, both specific to the site's shared
 * <custom-video> component:
 *
 * 1. Play/pause goes through that component's own public API
 *    (isPlaying/isInitialized/toggleVideo()) instead of the raw <video>
 *    element. The component already autoplays its muted background video
 *    on its own as soon as it nears the viewport, and it also pauses any
 *    other playing instance when a new one starts (a sitewide "only one
 *    video plays at a time" rule). Calling video.play()/pause() directly
 *    would race with the component's own IntersectionObserver and state,
 *    leaving the video paused despite our overlay being faded in.
 *
 * 2. The component lazy-swaps a <template> placeholder for the real
 *    <video> element itself, via its own IntersectionObserver (triggers
 *    ~200px before the element enters the viewport) — so the <video>
 *    usually doesn't exist yet when this script first binds at page load.
 *    Caching "the video element" once at bind time and bailing out if it
 *    isn't there yet means the feature silently never activates on a
 *    normal first scroll. So every play/pause/attribute step below looks
 *    the <video> up fresh, on demand, instead of caching it once.
 *
 * If the browser/OS blocks autoplay outright (confirmed on real devices:
 * iOS Low Power Mode blocks even muted autoplay), the block simply falls
 * back to showing the plain image instead of a frozen video frame.
 */
(function () {
  'use strict';

  if (window.__mobileScrollVideoInit) return;
  window.__mobileScrollVideoInit = true;

  // Desktop / hover-capable pointers keep the existing hover behavior only.
  if (window.matchMedia && window.matchMedia('(hover: hover)').matches) {
    return;
  }

  if (!('IntersectionObserver' in window)) return;

  var ACTIVATE_RATIO = 0.7;
  var DEACTIVATE_RATIO = 0.3;
  var ACTIVATE_DELAY_MS = 2000;
  var ACTIVE_CLASS = 'is-mobile-video-active';
  var BOUND_ATTR = 'data-mobile-scroll-autoplay-bound';
  var SELECTOR = '[data-mobile-scroll-autoplay="true"]';

  function bind(el) {
    if (el.hasAttribute(BOUND_ATTR)) return;
    el.setAttribute(BOUND_ATTR, 'true');

    var customVideoEl = el.querySelector('custom-video');
    if (!customVideoEl) return;

    // Looked up on demand (see note 2 above) rather than cached once —
    // the <video> element itself may not exist yet at bind time.
    function ensureMediaAttrs() {
      var video = customVideoEl.querySelector('video');
      if (video) {
        // Belt-and-suspenders: guarantee silent, inline playback on
        // iOS/Android regardless of what the rendered <video> already
        // carries (it's normally already set server-side via the
        // existing video_tag settings).
        video.muted = true;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
      }
      return video;
    }

    var timer = null;
    var active = false;

    function clearTimer() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    }

    function activate() {
      clearTimer();
      if (active) return;
      active = true;
      el.classList.add(ACTIVE_CLASS);
      var video = ensureMediaAttrs();
      // The shared component already starts this muted background video on
      // its own once it's near the viewport; only resume it ourselves if
      // something paused it in the meantime (our own deactivate(), or the
      // component's sitewide "pause other videos" rule), and only once the
      // component has actually finished creating the <video>.
      if (customVideoEl.isInitialized && !customVideoEl.isPlaying) {
        customVideoEl.toggleVideo();
      }
      // Last-resort fallback: if the component's own state says "playing"
      // but the underlying <video> is still paused (its internal play()
      // promise silently rejected, or state and reality drifted apart for
      // some other reason), nudge the raw element directly rather than
      // leaving the viewer looking at a frozen frame.
      setTimeout(function () {
        if (!active) return;
        var v = video || customVideoEl.querySelector('video');
        if (v && v.paused) {
          var playResult = v.play();
          if (playResult && typeof playResult.catch === 'function') {
            playResult.catch(function () {
              // The browser/OS refused autoplay outright (e.g. iOS Low
              // Power Mode). Back out of the active state so the base
              // image stays visible instead of a frozen video frame.
              if (active) {
                active = false;
                el.classList.remove(ACTIVE_CLASS);
              }
            });
          }
        }
      }, 400);
    }

    function deactivate() {
      clearTimer();
      if (!active) return;
      active = false;
      // Snap straight back to the base image when the viewer scrolls away —
      // no fade-out here (the fade is only for the image-to-video entrance).
      var overlay = el.querySelector('.hover-overlay');
      if (overlay) {
        overlay.style.transition = 'none';
      }
      el.classList.remove(ACTIVE_CLASS);
      if (overlay) {
        // Force layout so the removal above is applied instantly, then
        // restore the normal transition for the next activate().
        void overlay.offsetWidth;
        overlay.style.transition = '';
      }
      if (customVideoEl.isPlaying) {
        customVideoEl.toggleVideo();
      }
    }

    var observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var ratio = entries[i].intersectionRatio;
          if (ratio >= ACTIVATE_RATIO) {
            if (!active && !timer) {
              timer = setTimeout(activate, ACTIVATE_DELAY_MS);
            }
          } else if (ratio <= DEACTIVATE_RATIO) {
            deactivate();
          } else if (!active) {
            // Dropped below 70% before the delay elapsed: not "continuously"
            // in view, so cancel the pending activation.
            clearTimer();
          }
        }
      },
      { threshold: [0, DEACTIVATE_RATIO, ACTIVATE_RATIO, 1] }
    );

    observer.observe(el);
  }

  function init() {
    var els = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < els.length; i++) {
      bind(els[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-scan when the theme editor swaps section markup (new/duplicated
  // blocks), so the feature keeps working in live preview.
  document.addEventListener('shopify:section:load', init);
})();
